import React from 'react';
import { Dialog, DialogContent } from "src/@/components/ui/dialog";
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import axiosInstance from 'src/utils/axios';
import useAuth from 'src/hooks/useAuth';

export default function ModalCall({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { t } = useTranslation('chat');

  const handleInterest = async (type) => {
    try {
      await axiosInstance.post('/api/v1/global/feedback', {
        type,
        user: {
          name: user?.displayName || user?.name,
          email: user?.email,
          clienteId: user?.clienteId
        }
      });
      enqueueSnackbar(t('modalCall.feedbackThanks', { type }), {
        variant: 'success'
      });
      onClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t('modalCall.feedbackError'), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white border-0 shadow-2xl p-0 overflow-hidden text-gray-900">
        {/* Header Section */}
        <div className="relative bg-gradient-to-br from-indigo-600 to-violet-700 p-8 text-center overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
            <Iconify icon="fluent:call-connecting-20-regular" className="w-64 h-64 absolute -top-10 -right-10 text-white" />
          </div>
          <div className="relative z-10 flex flex-col items-center justify-center space-y-3">
            <div className="p-3 bg-white/20 backdrop-blur-md rounded-full ring-4 ring-white/10 shadow-xl">
              <Iconify icon="fluent:call-24-filled" className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white tracking-tight">{t('modalCall.comingSoonTitle')}</h2>
            <p className="text-indigo-100 font-medium text-sm max-w-xs mx-auto leading-relaxed">
              {t('modalCall.comingSoonDescription')}
            </p>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6 space-y-6">
          <div className="text-center space-y-2">
            <h3 className="text-base font-semibold text-gray-900">
              {t('modalCall.whichFeature')}
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed max-w-sm mx-auto">
              {t('modalCall.whichFeatureDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => handleInterest(t('modalCall.voipCalls'))}
              className="group relative flex items-center p-4 rounded-xl border border-gray-200 hover:border-indigo-500 bg-white hover:bg-slate-50 transition-all duration-300 text-left shadow-sm hover:shadow-md"
            >
              <div className="p-3 rounded-lg bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100 transition-colors mr-4">
                <Iconify icon="fluent:call-connecting-20-filled" className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <span className="block font-semibold text-gray-900 text-sm">{t('modalCall.voipTitle')}</span>
                <span className="block text-xs text-gray-500 mt-1">{t('modalCall.voipDescription')}</span>
              </div>
              <Iconify icon="akar-icons:arrow-right" className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 transition-colors transform group-hover:translate-x-1 duration-300" />
            </button>

            <button
              onClick={() => handleInterest(t('modalCall.whatsappCalls'))}
              className="group relative flex items-center p-4 rounded-xl border border-gray-200 hover:border-green-500 bg-white hover:bg-green-50 transition-all duration-300 text-left shadow-sm hover:shadow-md"
            >
              <div className="p-3 rounded-lg bg-green-50 text-green-600 group-hover:bg-green-100 transition-colors mr-4">
                <Iconify icon="logos:whatsapp-icon" className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <span className="block font-semibold text-gray-900 text-sm">{t('modalCall.whatsappTitle')}</span>
                <span className="block text-xs text-gray-500 mt-1">{t('modalCall.whatsappDescription')}</span>
              </div>
              <Iconify icon="akar-icons:arrow-right" className="w-4 h-4 text-gray-300 group-hover:text-green-500 transition-colors transform group-hover:translate-x-1 duration-300" />
            </button>
          </div>
        </div>

        <div className="px-6 pb-6 text-center">
          <button
            onClick={onClose}
            className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors py-2 px-4 rounded-full hover:bg-gray-100"
          >
            {t('modalCall.notNow')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
